// AI-Recall follow-up cadence settings — pure helpers (Phase 5).
//
// Backing store: teamio.settings
//   AI_RECALL_ACTIVATED  'TRUE' | 'FALSE'   — the on/off switch, DEFAULT OFF
//   AI_RECALL_SETTINGS   JSON blob          — channelOrder / email.enabled /
//                                             email.delayHours / quietHours
//
// WHY THE SAVE PATH IS CAREFUL (two real hazards, both silent):
//
//  1. The BACKEND treats a malformed blob as OFF and logs at error level, deliberately —
//     half-parsed outreach config must never be guessed at. So a UI bug that writes
//     invalid JSON does not error: it silently DISABLES the feature for that practice.
//     `buildSavePayload` therefore round-trips its own output through `parseCadence`
//     and refuses to return a payload that wouldn't read back as active.
//
//  2. `PropertyValue` is the column class that silently truncated a 7KB blob and took
//     Smile Bar's booking page down. We keep this blob small and check its length.
//
// READ-MODIFY-WRITE: unknown keys in the stored blob are PRESERVED. A future phase may add
// keys this build doesn't know about; clobbering them on every save would quietly delete
// another feature's config.
//
// Use `customer/upsertSettings` to persist — NOT `customer/saveSettings`, which bulk-deletes
// (see the comment on that action in src/store/customer.js).
//
// KEEP IN SYNC WITH:
//   - teamio-bot/lib/autorecall/recallCadenceSettings.js  (the authority — same defaults)
//   - cypress/node-tests/ai-recall-cadence.test.js         (mirrors this logic)

export const ACTIVATED_KEY = 'AI_RECALL_ACTIVATED';
export const SETTINGS_KEY = 'AI_RECALL_SETTINGS';

// Mirrors DEFAULT_CADENCE in the backend module. If these drift the UI will show a practice
// one thing and the sweep will do another.
export const CADENCE_DEFAULTS = Object.freeze({
  channelOrder: ['sms', 'email'],
  emailEnabled: true,
  delayHours: 24,
  maxDelayHours: 336, // ~2 weeks; beyond this the recall cycle has moved on
  quietStart: '',
  quietEnd: '',
});

const VALID_CHANNELS = ['sms', 'email'];
// Conservative ceiling — this blob should stay tiny. Smile Bar's was 7KB.
export const MAX_BLOB_BYTES = 4000;

const isHhMm = (v) => typeof v === 'string' && /^\d{1,2}:\d{2}$/.test(v.trim());

/**
 * Read the stored settings into a flat form model. Never throws — a corrupt blob yields
 * defaults with `corrupt: true` so the UI can warn instead of silently showing defaults
 * (the practice's real config being unreadable is worth surfacing, not papering over).
 *
 * @param {string|null} activatedRaw  AI_RECALL_ACTIVATED PropertyValue
 * @param {string|null} settingsRaw   AI_RECALL_SETTINGS PropertyValue
 */
export function parseCadence(activatedRaw, settingsRaw) {
  const activated = typeof activatedRaw === 'string' && activatedRaw.trim().toUpperCase() === 'TRUE';

  const form = {
    activated,
    emailEnabled: CADENCE_DEFAULTS.emailEnabled,
    delayHours: CADENCE_DEFAULTS.delayHours,
    channelOrder: CADENCE_DEFAULTS.channelOrder.slice(),
    quietStart: CADENCE_DEFAULTS.quietStart,
    quietEnd: CADENCE_DEFAULTS.quietEnd,
    corrupt: false,
    unknownKeys: {},
  };

  const raw = settingsRaw == null ? '' : String(settingsRaw).trim();
  if (raw === '') return form;

  let parsed;
  try {
    parsed = JSON.parse(raw);
  } catch (e) {
    form.corrupt = true;
    return form;
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    form.corrupt = true;
    return form;
  }

  const email = parsed.email && typeof parsed.email === 'object' ? parsed.email : {};
  if (typeof email.enabled === 'boolean') form.emailEnabled = email.enabled;
  if (Number.isFinite(Number(email.delayHours))) form.delayHours = Number(email.delayHours);

  if (Array.isArray(parsed.channelOrder)) {
    const cleaned = parsed.channelOrder
      .map((c) => (typeof c === 'string' ? c.trim().toLowerCase() : ''))
      .filter((c, i, a) => VALID_CHANNELS.includes(c) && a.indexOf(c) === i);
    if (cleaned.length) form.channelOrder = cleaned;
  }

  if (parsed.quietHours && typeof parsed.quietHours === 'object') {
    if (isHhMm(parsed.quietHours.start)) form.quietStart = parsed.quietHours.start.trim();
    if (isHhMm(parsed.quietHours.end)) form.quietEnd = parsed.quietHours.end.trim();
  }

  // Preserve anything this build doesn't understand so a save can't delete it.
  for (const k of Object.keys(parsed)) {
    if (!['email', 'sms', 'channelOrder', 'quietHours'].includes(k)) form.unknownKeys[k] = parsed[k];
  }
  return form;
}

/**
 * Validate the form. Returns an array of human-readable errors (empty = OK).
 * The delay is deliberately permissive at 0 — "send as soon as the sweep sees it" is a
 * legitimate choice — but never negative, non-numeric, or beyond the backend's ceiling,
 * because the backend clamps those and the practice would see a different number than it set.
 */
export function validateCadence(form) {
  const errors = [];
  const f = form || {};

  const d = Number(f.delayHours);
  if (!Number.isFinite(d)) errors.push('Delay must be a number of hours.');
  else if (d < 0) errors.push('Delay cannot be negative.');
  else if (d > CADENCE_DEFAULTS.maxDelayHours) {
    errors.push(`Delay cannot exceed ${CADENCE_DEFAULTS.maxDelayHours} hours (about 2 weeks).`);
  }

  const s = (f.quietStart || '').trim();
  const e = (f.quietEnd || '').trim();
  if ((s && !e) || (e && !s)) errors.push('Set both a start and end time for quiet hours, or neither.');
  if (s && !isHhMm(s)) errors.push('Quiet hours start must look like 20:00.');
  if (e && !isHhMm(e)) errors.push('Quiet hours end must look like 08:00.');
  if (s && e && isHhMm(s) && isHhMm(e) && s.trim() === e.trim()) {
    errors.push('Quiet hours start and end cannot be the same time.');
  }

  if (f.activated && f.emailEnabled && Array.isArray(f.channelOrder) && !f.channelOrder.includes('email')) {
    // The backend honors channelOrder MEMBERSHIP, so this combination silently sends nothing.
    errors.push('Email is switched on but is not one of the selected channels.');
  }
  return errors;
}

/**
 * Build the settings payload for `customer/upsertSettings`.
 *
 * SETTINGS ARE NOT SAFE TO GUESS AT. Three refusals here, all of which prevent this editor
 * from destroying configuration the practice never intended to change:
 *
 *  1. `settingsLoaded` — if the site's settings haven't actually been fetched, `parseCadence`
 *     legitimately returns DEFAULTS, and saving those would silently overwrite a practice's
 *     real delay / quiet hours / channels with values they never chose. An unloaded editor
 *     must never be able to write. This is the dangerous one: it looks like a normal save.
 *  2. `confirmedReplaceCorrupt` — an unreadable blob means we could not recover the
 *     practice's existing config, so a save DISCARDS it. That needs explicit intent, not a
 *     dismissible warning.
 *  3. Round-trip — the blob is re-parsed before returning, because the backend reads an
 *     unreadable blob as OFF, so a UI bug would disable the feature rather than error.
 *
 * Unknown keys in the stored blob are preserved (read-modify-write), so a future phase's
 * config isn't deleted by this editor saving.
 *
 * @param {object} form
 * @param {{settingsLoaded?: boolean, confirmedReplaceCorrupt?: boolean}} [opts]
 * @returns {{ok: true, settings: object} | {ok: false, errors: string[]}}
 */
export function buildSavePayload(form, opts = {}) {
  const f0 = form || {};

  // (0) CROSS-SITE SAFETY. teamio.settings is keyed UNIQUE on (SiteID, Property), so every
  //     write must carry the exact site. A blank/undefined siteId here would either 400 at
  //     the API or — worse in any future caller — write against the wrong row. The editor
  //     must not be able to produce a site-less payload at all.
  const siteId = typeof opts.siteId === 'string' ? opts.siteId.trim() : '';
  if (!siteId) {
    return { ok: false, errors: ['No office selected. Reopen this window and try again.'] };
  }

  // (1) Never write defaults over real config we simply haven't read yet.
  if (opts.settingsLoaded === false) {
    return { ok: false, errors: ["This office's settings haven't loaded yet. Reopen this window and try again."] };
  }

  // (2) Replacing unreadable config is destructive — require explicit intent.
  if (f0.corrupt && !opts.confirmedReplaceCorrupt) {
    return { ok: false, errors: ['The existing settings could not be read. Tick the confirmation to replace them.'] };
  }

  const errors = validateCadence(form);
  if (errors.length) return { ok: false, errors };

  const f = form || {};
  const blob = {
    // Unknown keys FIRST so the fields we own always win on conflict.
    ...(f.unknownKeys || {}),
    channelOrder: Array.isArray(f.channelOrder) && f.channelOrder.length
      ? f.channelOrder.slice()
      : CADENCE_DEFAULTS.channelOrder.slice(),
    email: {
      enabled: !!f.emailEnabled,
      delayHours: Number(f.delayHours),
    },
  };

  const s = (f.quietStart || '').trim();
  const e = (f.quietEnd || '').trim();
  if (s && e) blob.quietHours = { start: s, end: e };

  const serialized = JSON.stringify(blob);
  if (serialized.length > MAX_BLOB_BYTES) {
    return { ok: false, errors: ['These settings are too large to save. Remove some custom values.'] };
  }

  // Prove the blob reads back as intended. A UI bug that produced unreadable JSON would
  // otherwise disable the feature silently on the next sweep.
  const roundTrip = parseCadence(f.activated ? 'TRUE' : 'FALSE', serialized);
  if (roundTrip.corrupt
    || roundTrip.emailEnabled !== !!f.emailEnabled
    || roundTrip.delayHours !== Number(f.delayHours)) {
    return { ok: false, errors: ['Could not save these settings safely. Please try again.'] };
  }

  return {
    ok: true,
    // siteId echoed back so the caller dispatches with the SAME site this payload was built
    // for — it cannot drift between build and dispatch.
    siteId,
    settings: {
      [ACTIVATED_KEY]: f.activated ? 'TRUE' : 'FALSE',
      [SETTINGS_KEY]: serialized,
    },
  };
}

/**
 * One-line summary for the panel header, so a practice can see the effective behavior
 * without opening the editor.
 */
export function describeCadence(form) {
  const f = form || {};
  if (!f.activated) return 'Off';
  if (!f.emailEnabled) return 'On — email follow-up off';
  const d = Number(f.delayHours);
  const when = d === 0 ? 'immediately' : `after ${d} hour${d === 1 ? '' : 's'}`;
  const quiet = f.quietStart && f.quietEnd ? `, quiet ${f.quietStart}–${f.quietEnd}` : '';
  return `On — email ${when}${quiet}`;
}
